import { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Space, Spin, Typography } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { QRSigningClientCMS } from "sigex-qr-signing-client";
import { ApiService } from "../api";

const { Text } = Typography;

type Props = {
  requestId?: string;
  documentLabel?: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  // Receives the PEM-armored base64 CMS, ready for the approve endpoint — the
  // same value shape the NCALayer path submits.
  onSigned: (sign: string) => Promise<void>;
};

type Phase = "preparing" | "awaiting" | "finishing" | "canceled" | "error";

/**
 * EDS signing via eGov Mobile, as an alternative to the NCALayer desktop app.
 *
 * We hand the request's document to SIGEX (sigex.kz), which relays it to the
 * signer's eGov Mobile / eGov Business app: the user scans the QR (or, on a
 * phone, taps the launch link), reviews and signs with their mobile key, and
 * SIGEX returns the CMS. The resulting signature is submitted through the very
 * same approve endpoint NCALayer uses, so the backend is unchanged.
 */
const EgovMobileSignModal = ({
  requestId,
  documentLabel,
  visible,
  setVisible,
  onSigned,
}: Props) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("preparing");
  const [qrCode, setQrCode] = useState<string>("");
  const [mobileLink, setMobileLink] = useState<string>("");
  const [businessLink, setBusinessLink] = useState<string>("");
  const [error, setError] = useState<string>("");
  // Bumped to re-run the flow after a cancel/error without closing the modal.
  const [attempt, setAttempt] = useState<number>(0);
  // A run keeps polling SIGEX even after the modal is closed (there is no
  // cancel API); this guard stops a stale run from writing state or, worse,
  // recording an approval the user has walked away from.
  const runIdRef = useRef<number>(0);

  useEffect(() => {
    if (!visible) return;

    const runId = ++runIdRef.current;
    setPhase("preparing");
    setQrCode("");
    setMobileLink("");
    setBusinessLink("");
    setError("");

    const label = documentLabel || t("Permit to Work document");

    (async () => {
      try {
        // attach=true → CMS_WITH_DATA (attached CMS), matching the NCALayer
        // basicsCMSParamsAttached signatures already stored for EDS approvals.
        const client = new QRSigningClientCMS(label, true);

        const fileData = await ApiService.apiMainGetRequestDocRetrieve(
          parseInt(requestId!)
        );
        // names is [ru, kk, en]; a single label is accepted and shown as-is.
        await client.addDataToSign([label], fileData, [], false);

        const qr = await client.registerQRSinging();
        if (runId !== runIdRef.current) return;
        setQrCode(qr);
        setMobileLink(client.getEGovMobileLaunchLink());
        setBusinessLink(client.getEGovBusinessLaunchLink());
        setPhase("awaiting");

        // Long-polls SIGEX; resolves once the app returns the signature.
        const signatures = await client.getSignatures();
        if (runId !== runIdRef.current) return;
        const cms = signatures?.[0];
        if (!cms) throw new Error(t("No signature was returned."));

        setPhase("finishing");
        // SIGEX returns raw base64; the approve endpoint strips the first/last
        // lines (clean_CMS_signature), so wrap in PEM armor to land the same
        // clean base64 the NCALayer path stores.
        await onSigned(`-----BEGIN CMS-----\n${cms}\n-----END CMS-----`);
        if (runId !== runIdRef.current) return;
        setVisible(false);
      } catch (e: any) {
        if (runId !== runIdRef.current) return;
        if (e?.canceledByUser) {
          setPhase("canceled");
        } else {
          setError(e?.message ? String(e.message) : String(e));
          setPhase("error");
        }
      }
    })();
    // Re-running on `attempt` is intentional (the retry button).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, attempt]);

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const linkTarget = isMobile ? "_self" : "_blank";

  const footer = [
    <Button key="close" onClick={() => setVisible(false)}>
      {t("Close")}
    </Button>,
    phase === "error" || phase === "canceled" ? (
      <Button key="retry" type="primary" onClick={() => setAttempt((a) => a + 1)}>
        {t("Retry")}
      </Button>
    ) : null,
  ];

  return (
    <Modal
      title={t("Sign with eGov Mobile")}
      open={visible}
      onCancel={() => setVisible(false)}
      centered
      footer={footer}
    >
      {phase === "preparing" && (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", padding: "24px 0" }}
        >
          <Spin />
          <Text type="secondary">{t("Preparing document…")}</Text>
        </Space>
      )}

      {phase === "awaiting" && (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", padding: "8px 0" }}
        >
          <Text type="secondary" style={{ textAlign: "center" }}>
            {t(
              "Scan this QR code with the eGov Mobile app, then confirm the signature."
            )}
          </Text>
          {qrCode && (
            <img
              src={`data:image/gif;base64,${qrCode}`}
              alt="eGov Mobile QR"
              style={{ width: 240, height: 240 }}
            />
          )}
          {(mobileLink || businessLink) && (
            <Space wrap>
              {mobileLink && (
                <Button
                  icon={<MobileOutlined />}
                  href={mobileLink}
                  target={linkTarget}
                >
                  {t("Open in eGov Mobile")}
                </Button>
              )}
              {businessLink && (
                <Button href={businessLink} target={linkTarget}>
                  {t("Open in eGov Business")}
                </Button>
              )}
            </Space>
          )}
          <Space>
            <Spin size="small" />
            <Text type="secondary">{t("Waiting for signature…")}</Text>
          </Space>
        </Space>
      )}

      {phase === "finishing" && (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", padding: "24px 0" }}
        >
          <Spin />
          <Text type="secondary">{t("Recording approval…")}</Text>
        </Space>
      )}

      {phase === "canceled" && (
        <Alert
          type="warning"
          showIcon
          message={t("Signing was cancelled in the app.")}
        />
      )}

      {phase === "error" && (
        <Alert
          type="error"
          showIcon
          message={t("Could not complete eGov Mobile signing.")}
          description={error}
        />
      )}
    </Modal>
  );
};

export default EgovMobileSignModal;
